/* What a route loader is allowed to wait for.
 *
 * A loader's job is to have *started* the reads an address needs, so they overlap instead
 * of queueing behind each other's components. It is not to decide whether they succeeded:
 * a route that throws when a door is down takes the whole shell with it, and standing rule
 * 13 says every address survives one of its doors being down. The failure stays in the
 * query cache, where the thing that owns it — the session gate, a workspace gate, a region
 * on a page — already knows how to show it with a retry.
 *
 * So a loader awaits an *answer*, not a success — and a page's loader awaits nothing. This
 * is a module because the alternative is four route files each spelling a bare `.catch`
 * that a reviewer has to re-derive the reason for. */

import type { QueryClient } from "@tanstack/react-query";

import type { Project, ProjectId } from "./account";
import type { ProjectAddress } from "./nav";
import { authQueryKeys } from "./query-keys";

/** Await a prefetch for whatever it resolves to: its data, or nothing because it failed. */
export const started = <T>(read: Promise<T>): Promise<T | undefined> =>
  read.catch(() => undefined);

/** Start the reads a project page needs the moment its address is chosen, and return at
 * once.
 *
 * A project page's loader runs beside `_console`'s, so the reads it starts overlap the
 * page's own chunk instead of queueing behind its first render. Nothing here holds the
 * navigation: the router draws once every loader has returned, so a loader that waited —
 * even for the portfolio, which turns the slug pair into an id — would hold the whole
 * frame on a cold load. So the resolution runs on its own: the portfolio (in memory on
 * every navigation after the first, and the read `_console` started on a cold load), the
 * small module that spells the reads (imported dynamically because a loader is not
 * code-split and must not pull a workspace into the first paint), then the reads. A
 * project this identity does not hold starts nothing; `ProjectScope` says so. */
export function prefetchForProject<Reads>(
  queryClient: QueryClient,
  address: ProjectAddress,
  reads: Promise<Reads>,
  start: (reads: Reads, projectId: ProjectId) => void,
): void {
  void started(
    (async () => {
      const [project, module] = await Promise.all([
        addressedProject(queryClient, address),
        started(reads),
      ]);
      if (project !== undefined && module !== undefined) start(module, project.id);
    })(),
  );
}

/** The project an address names in the portfolio this identity holds, never a disabled
 *  one: the one answer every loader gives to "which project is this address". */
export function projectAt(
  projects: readonly Project[] | undefined,
  address: ProjectAddress,
): Project | undefined {
  return projects?.find(
    (candidate) =>
      !candidate.disabled &&
      candidate.organizationSlug === address.org &&
      candidate.slug === address.project,
  );
}

/** The addressed project from the portfolio already in memory, without a request. Keyed
 *  as `projectsQuery` keys it; that module carries its door client, which a loader's
 *  first-paint half must not load. */
export function cachedProjectAt(
  queryClient: QueryClient,
  principalId: string | undefined,
  address: ProjectAddress,
): Project | undefined {
  if (principalId === undefined) return undefined;
  return projectAt(
    queryClient.getQueryData<readonly Project[]>(
      authQueryKeys.principal(principalId, "projects"),
    ),
    address,
  );
}

/** The addressed project, reading the portfolio when it is not here yet (on a cold load,
 *  the read `_console` already started). */
export async function addressedProject(
  queryClient: QueryClient,
  address: ProjectAddress,
): Promise<Project | undefined> {
  const [{ loadedSession }, { projectsQuery }] = await Promise.all([
    import("./session"),
    import("./account"),
  ]);
  const principalId = loadedSession(queryClient)?.principal.id;
  if (principalId === undefined) return undefined;
  return projectAt(
    await started(queryClient.ensureQueryData(projectsQuery(principalId))),
    address,
  );
}
